// src/data/worksheets/discipline.js — performance-side pair (authored).
// Built off the Self-Discipline action plan: same time same place, make it easy to start, track it.

export const DISCIPLINE_CHECKLIST = {
    id: 'discipline_checklist', topicId: 'self-discipline', kind: 'checklist',
    title: 'Self-Discipline Checklist',
    intro: 'Discipline is mostly design. Set things up so the right thing is the easy thing.',
    sections: [
        { id: 'anchor', title: 'Anchor the Habit', prompts: [
            { id: 'sametime', type: 'checklist', label: 'Same Time, Same Place', items: [
                { id: 's1', label: 'Attach the habit to something you already do every day (after coffee, before the drive, right {after training|after work}).' },
                { id: 's2', label: 'Give it a fixed slot in the day — decisions made in advance don\'t need willpower.' }
            ] },
            { id: 'onehabit', type: 'checklist', label: 'One Habit at a Time', items: [
                { id: 'o1', label: 'Pick the single habit that would change the most if it were consistent.' },
                { id: 'o2', label: 'Leave the rest alone until this one is automatic (two to three weeks).' }
            ] }
        ] },
        { id: 'friction', title: 'Make It Easy to Start', prompts: [
            { id: 'prep', type: 'checklist', label: 'Prepare the Night Before', items: [
                { id: 'p1', label: 'Lay things out so the first step is already done ({kit by the door|bag by the door}, files open, food ready).' },
                { id: 'p2', label: 'Decide exactly what "done" looks like tomorrow — one sentence.' }
            ] },
            { id: 'small', type: 'checklist', label: 'Shrink the First Step', items: [
                { id: 'sm1', label: 'Make the starting version two minutes long. You can always keep going.' },
                { id: 'sm2', label: 'On bad days, do the two-minute version and count it.' }
            ] },
            { id: 'remove', type: 'checklist', label: 'Add Friction to the Wrong Thing', items: [
                { id: 'rm1', label: 'Put the distraction one extra step away (phone in another room, app logged out, snacks out of sight).' },
                { id: 'rm2', label: 'Make the default the good choice.' }
            ] }
        ] },
        { id: 'track', title: 'Track It', prompts: [
            { id: 'visible', type: 'checklist', label: 'Make Streaks Visible', items: [
                { id: 'v1', label: 'A simple tick on a calendar makes streaks visible and harder to break.' },
                { id: 'v2', label: 'Never miss twice — one miss is a blip, two is the start of a new habit.' }
            ] },
            { id: 'review', type: 'checklist', label: 'Check the Design, Not the Willpower', items: [
                { id: 'r1', label: 'When you miss, ask what made it hard — then change that, not yourself.' },
                { id: 'r2', label: 'Move the slot, shrink the step, or remove a distraction. One tweak at a time.' }
            ] }
        ] },
        { id: 'pressure', title: 'When Motivation Is Gone', prompts: [
            { id: 'identity', type: 'checklist', label: 'Show Up Anyway', items: [
                { id: 'i1', label: 'Separate the decision from the feeling — the slot is the slot.' },
                { id: 'i2', label: 'Tell yourself "I\'m someone who does this" and let the action prove it.' }
            ] },
            { id: 'people', type: 'checklist', label: 'Borrow Structure', items: [
                { id: 'pp1', label: 'Pair up with someone for the habit you struggle with most.' },
                { id: 'pp2', label: 'Tell one person your streak target.' }
            ] }
        ] },
        { id: 'reflect', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'wk1', label: 'Count the ticks. Celebrate the streak, however short.' },
                { id: 'wk2', label: 'Make one design tweak for next week.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'You don\'t need more willpower. You need a better setup and a visible streak.'
};

export const DISCIPLINE_WORKSHEET = {
    id: 'discipline_worksheet', topicId: 'self-discipline', kind: 'worksheet',
    title: 'Self-Discipline Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'level', type: 'scale', label: 'How consistent have you been over the past week?', help: 'Following through when you didn\'t feel like it.', min: 1, max: 10, minLabel: 'All over the place', maxLabel: 'Like clockwork' },
            { id: 'misses', type: 'list', label: 'What do you keep meaning to do and not doing?', count: 3 },
            { id: 'whenmiss', type: 'list', label: 'When do you tend to miss?', help: 'Time of day, mood, what came before.', count: 3 }
        ] },
        { id: 'understanding', title: 'Understanding Your Follow-Through', prompts: [
            { id: 'works', type: 'text', label: 'A habit you already keep without effort — why does it stick?', help: 'The slot, the trigger, the setup. Copy the pattern.', multiline: true },
            { id: 'friction', type: 'list', label: 'What makes the missed habits hard to start?', help: 'Be practical: too long, no clear start, wrong time, distractions nearby.', count: 3 }
        ] },
        { id: 'building', title: 'Designing the Habit', prompts: [
            { id: 'habit', type: 'text', label: 'The one habit you are building', help: 'Just one for the next two to three weeks.' },
            { id: 'anchor', type: 'text', label: 'Anchor: after / before what, and where?', help: 'e.g., "right after morning coffee, at the kitchen table".' },
            { id: 'twomin', type: 'text', label: 'The two-minute version' },
            { id: 'done', type: 'text', label: 'What "done" looks like — one sentence' },
            { id: 'prep', type: 'list', label: 'Night-before setup', help: 'The things you will lay out so the first step is already done.', count: 3 },
            { id: 'friction_rules', type: 'list', label: 'Friction you will add to the wrong thing', count: 2 }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'streak', type: 'text', label: 'Streak target for this week', help: 'e.g., "5 of 7 days".' },
            { id: 'partner', type: 'text', label: 'Who you will tell (or pair up with)' },
            { id: 'missrule', type: 'text', label: 'Your never-miss-twice rule', help: 'What you will do the day after a miss.' }
        ] },
        { id: 'track', title: 'Track It', description: 'A tick a day. The chart is your streak.', prompts: [
            { id: 'tracker', type: 'table', label: 'Habit tracker', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'done', label: 'Done? (1 = yes, 0 = no)', kind: 'number', min: 0, max: 1 },
                { id: 'consistency', label: 'Consistency today (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'friction', label: 'What made it hard / easy', kind: 'text' },
                { id: 'notes', label: 'Notes', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'consistency', label: 'Consistency' }, { id: 'done', label: 'Done' }] } }
        ] }
    ],
    outro: 'Consistency compounds. Protect the streak, tweak the design, never miss twice.'
};

export const DISCIPLINE_WORKSHEETS = [DISCIPLINE_CHECKLIST, DISCIPLINE_WORKSHEET];
